/**
 * Notification Service
 * Event Master v1.5 - Real API Integration (NO MOCKS)
 */

import api from './api';
import type {
  INotification,
  INotificationResponse, 
  INotificationFilters,
  INotificationSettings
} from '../types/notifications';

// Export types for hooks
export type GetNotificationsResponse = INotificationResponse;

class NotificationService {
  /**
   * Get notifications with filters
   */
  async getNotifications(filters: INotificationFilters): Promise<INotificationResponse> {
    try {
      // Преобразуем фильтры для совместимости с API
      const apiParams: any = { ...filters };
      
      // Бекенд не поддерживает isRead, используем status
      // Удаляем isRead если он есть
      if (apiParams.isRead !== undefined) {
        delete apiParams.isRead;
      }
      // Приводим массивы к строке через запятую, чтобы избежать status[]=unread
      if (Array.isArray(apiParams.status)) {
        apiParams.status = apiParams.status.join(',');
      }
      // Если статус передан пустым массивом/пустой строкой — удаляем параметр
      if (apiParams.status === '' || (Array.isArray(apiParams.status) && apiParams.status.length === 0)) {
        delete apiParams.status;
      }
      if (Array.isArray(apiParams.priority)) {
        apiParams.priority = apiParams.priority.join(',');
      }
      // Убираем пустые/неопределённые параметры, чтобы не слать priority=
      Object.keys(apiParams).forEach((key) => {
        const value = (apiParams as any)[key];
        if (value === undefined || value === null || value === '') {
          delete (apiParams as any)[key];
        }
      });
      
      try {
        const response = await api.get<INotificationResponse>('/notifications', {
          params: apiParams
        });
        return response.data;
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        // Если бекенд не принимает статус/фильтр, пробуем без фильтров (fallback)
        if (status === 400) {
          const fallback = await api.get<INotificationResponse>('/notifications');
          return fallback.data;
        }
        throw err;
      }
    } catch (error) {
      console.error('[NotificationService] Failed to get notifications:', error);
      throw error;
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string): Promise<INotification> {
    try {
      const response = await api.post<INotification>(`/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error('[NotificationService] Failed to mark as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    try {
      await api.patch('/notifications/read-all');
    } catch (error) {
      console.error('[NotificationService] Failed to mark all as read:', error);
      throw error;
    }
  }

  /**
   * Delete notification
   */
  async deleteNotification(notificationId: string): Promise<void> {
    try {
      await api.delete(`/notifications/${notificationId}`);
    } catch (error) {
      console.error('[NotificationService] Failed to delete notification:', error);
      throw error;
    }
  }

  /**
   * Get notification settings
   */
  async getNotificationSettings(): Promise<INotificationSettings[]> {
    try {
      const response = await api.get<INotificationSettings[]>('/notifications/settings');
      return response.data;
    } catch (error) {
      console.error('[NotificationService] Failed to get settings:', error);
      throw error;
    }
  }

  /**
   * Update notification settings
   */
  async updateNotificationSettings(settings: INotificationSettings): Promise<INotificationSettings> {
    try {
      const response = await api.put<INotificationSettings>('/notifications/settings', settings);
      return response.data;
    } catch (error) {
      console.error('[NotificationService] Failed to update settings:', error);
      throw error;
    }
  }

  /**
   * Get unread count
   */
  async getUnreadCount(): Promise<number> {
    try {
      const response = await api.get<{ count: number }>('/notifications/unread-count');
      return response.data.count;
    } catch (error: unknown) {
      const isNetworkError = !(error as { response?: unknown })?.response ||
        (error as { code?: string })?.code === 'ECONNREFUSED' ||
        (error as { code?: string })?.code === 'ERR_NETWORK';
      if (isNetworkError) {
        return 0;
      }
      console.error('[NotificationService] Failed to get unread count:', error);
      throw error;
    }
  }

  /**
   * Get notification by ID
   */
  async getNotificationById(id: string): Promise<INotification> {
    try {
      const response = await api.get<INotification>(`/notifications/${id}`);
      return response.data;
    } catch (error) {
      console.error('[NotificationService] Failed to get notification by id:', error);
      throw error;
    }
  }

  /**
   * Get notification preferences
   */
  async getPreferences(): Promise<INotificationSettings[]> {
    try {
      const response = await api.get<INotificationSettings[]>('/notifications/preferences');
      return response.data;
    } catch (error) {
      console.error('[NotificationService] Failed to get preferences:', error);
      throw error;
    }
  }

  /**
   * Send notification
   */
  async sendNotification(notification: Partial<INotification>): Promise<INotification> {
    try {
      const response = await api.post<INotification>('/notifications', notification);
      return response.data;
    } catch (error) {
      console.error('[NotificationService] Failed to send notification:', error);
      throw error;
    }
  }
}

const notificationService = new NotificationService();
export default notificationService;
